// libs
import React, { memo, useContext } from 'react';
import { styled } from '@nykaa/ui-components';
import isEmpty from 'lodash/isEmpty';

// global components
import Carousel from 'shared/components/Carousel';
import ProductBase from 'shared/components/Product';

// components
import CAVProduct from 'shared/layouts/DetailsPage/components/Widgets/components/CAVProduct';

// contexts
import ResponsiveContext from 'shared/components/ResponsiveLoader/ResponsiveContext';

// helpers
import { getCarouselProps, getCAVCarouselProps } from './helpers';
import NFA from 'shared/helpers/analytics/NFAnalytics';

// constants
import { NAV_STYLES } from './constants';
import { LOGIN_LOCATIONS, LOGIN_PAGE } from 'shared/helpers/analytics/constants/loginEvents';
import { PRODUCT_INTERACTION } from 'shared/helpers/analytics/constants';
import { WISHLIST_INTERACTION } from 'shared/helpers/analytics/constants/wishlist';

// defs
import { IProductList, IProduct } from 'shared/layouts/DetailsPage/components/Widgets/types';

interface CarouselChildrenProps {
  products: IProductList;
  width: number;
  isCAV: boolean;
  action_url?: string;
  widgetMetadata: Record<string, any>;
  onWishlistClick?: VoidFunction;
  onProductInteraction: VoidFunction;
}

const VIEW_ALL = 'VIEW ALL';
const VIEW_ALL_EVENT = 'view-all-clicked';

const Product = styled(ProductBase)`
  margin: ${({ theme }) => `${theme.spacing.spacing100} 0`};

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.shadow200};
  }
`;

interface IWidgetCarousel {
  products: IProductList;
  action_url?: string;
  isCAV?: boolean;
  widgetMetadata: Record<string, any>;
}

const ViewAllCtr = styled.div<{ width?: number }>`
  padding: ${({ theme }) => `${theme.spacing.spacing100} 0`};

  ${({ width }) =>
    width !== undefined &&
    `
    width: ${width}px;
  `}
`;

const ViewAllAnc = styled.a`
  text-decoration: none;
`;

const ViewAllPlaceholder = styled.div`
  background: #fff;
  width: 100%;
  padding: 62% 0;
  ${({ theme }) => theme.typography.subTitleLarge};
  text-align: center;
  border: 1px solid #ababab;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevations.shadow200};
  }
`;

const Underline = styled.span`
  border-bottom: 1px solid black;
`;

export function ViewAll({
  width,
  actionUrl,
  widgetMetadata,
}: {
  width: number;
  actionUrl: string;
  widgetMetadata: Record<string, any>;
}) {
  const handleClick = () => {
    NFA.track({
      event: VIEW_ALL_EVENT,
      data: {
        widgetType: widgetMetadata?.widgetType,
        listing_position: widgetMetadata?.widgetPosition,
      },
    });
  };

  return (
    <ViewAllCtr width={width} onClick={handleClick}>
      <ViewAllAnc href={actionUrl}>
        <ViewAllPlaceholder>
          <Underline>{VIEW_ALL}</Underline>
        </ViewAllPlaceholder>
      </ViewAllAnc>
    </ViewAllCtr>
  );
}

const CarouselChildren = ({
  products,
  width,
  isCAV,
  action_url = '',
  widgetMetadata,
  onWishlistClick,
  onProductInteraction,
}: CarouselChildrenProps) => {
  // currently consumed for both CAV and product props, hence cannot define a type/interface at the moment
  const ContainerProduct = ({ item, productPosition }: { item: any; productPosition: number }) => {
    return (
      <div style={{ width: `${width}px` }}>
        <Product
          item={item}
          key={item.id}
          width={width}
          openInTab
          showWishlist
          onWishlistClick={onWishlistClick}
          onInteraction={onProductInteraction}
          trackingMetadata={{ productPosition, ...widgetMetadata }}
        ></Product>
      </div>
    );
  };

  const RenderProduct = isCAV ? CAVProduct : ContainerProduct;

  const childrenArray = products.map((product: IProduct, index: number) => (
    <RenderProduct item={product} key={product.id} productPosition={index + 1} />
  ));

  if (action_url && !isEmpty(action_url)) {
    childrenArray.push(
      <ViewAll
        width={width}
        key={childrenArray.length}
        actionUrl={action_url}
        widgetMetadata={widgetMetadata}
      />
    );
  }

  return childrenArray;
};

function WidgetCarousel({ products, action_url, isCAV = false, widgetMetadata }: IWidgetCarousel) {
  const { deviceWidth } = useContext(ResponsiveContext);
  const carouselProps = isCAV ? getCAVCarouselProps(deviceWidth) : getCarouselProps(deviceWidth);
  const width = carouselProps.slideWidth;

  const onWishlistClick = () => {
    NFA.updateDataLayer({
      loginPage: LOGIN_PAGE.RECO_WIDGET,
      loginLocation: LOGIN_LOCATIONS.WISHLIST_ICON_PRODUCT,
      wishlistClickedLocation: WISHLIST_INTERACTION.RECO_WIDGET,
    });
  };

  const onProductInteraction = () => {
    NFA.updateDatalayerBeforeNext({
      productInteractedType: PRODUCT_INTERACTION.RECO_WIDGET,
      widgetTitle: widgetMetadata?.widgetTitle,
      widgetType: widgetMetadata?.widgetType,
    });
  };

  const childrenArray = CarouselChildren({
    products,
    width,
    isCAV,
    onWishlistClick,
    onProductInteraction,
    widgetMetadata,
    action_url,
  });

  return (
    <Carousel navCustomStyles={NAV_STYLES} {...carouselProps}>
      {childrenArray}
    </Carousel>
  );
}

export default memo(WidgetCarousel);
